import { createRouter, createWebHistory } from 'vue-router'

import { OrganizationKind } from '@/enums/organization'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

/*
 * The signed-in shell is exactly the viewport (`layouts/Default.vue`, `h-dvh`) and its `<main>`
 * is what scrolls — the window never does, so vue-router's own scroll handling, which only
 * ever moves the window, has nothing to move. The page column is marked `data-shell-main`, and
 * the scroll behaviour below moves THAT element itself and answers `false` to the router.
 *
 * The router's `savedPosition` is the window's, always zero here, so the column's own offset
 * is kept per address as the router leaves it (the guard below), and a back/forward
 * navigation — the only kind that has a `savedPosition` at all — puts it back. The Blank
 * layout has no such column, and there the window is scrolled as it always was.
 */
const shellMain = (): HTMLElement | null => document.querySelector<HTMLElement>('[data-shell-main]')

const shellScroll = new Map<string, number>()

/*
 * Which kinds of organisation a screen is for (`meta.kinds`, OrganizationKind): the
 * fleet's business is the operator's and the customers', never a workshop's; the
 * platform's own screens are the operator's. Absent is every kind.
 */
const FLEET    = [OrganizationKind.Operator, OrganizationKind.Customer]
const PLATFORM = [OrganizationKind.Operator]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        const main = shellMain()

        if (!main) {
            if (savedPosition)         return savedPosition
            if (to.hash)               return { el: to.hash }
            if (from.path === to.path) return {}

            return { top: 0, behavior: 'smooth' }
        }

        if (savedPosition) {
            main.scrollTo({ top: shellScroll.get(to.fullPath) ?? 0 })
        } else if (to.hash) {
            document.getElementById(decodeURIComponent(to.hash.slice(1)))?.scrollIntoView()
        } else if (from.path !== to.path) {
            main.scrollTo({ top: 0, behavior: 'smooth' })
        }

        return false
    },
    routes: [
        {
            path:     '/',
            redirect: '/map',
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        // The console's home: every vehicle on one map, with a list beside it saying
        // what each of them is doing. It is the first question anybody opens a fleet
        // console to ask, and the only screen here that wants the whole window —
        // `fullBleed` is what takes the layout's page container off it.
        {
            path:      '/map',
            name:      'map',
            component: () => import('@/views/map/FleetMap.vue'),
            meta:      { requiresAuth: true, fullBleed: true },
        },
        // The table behind the map: every tracker with its counts and its last packet,
        // which is what a device that has just been plugged in is checked on.
        {
            path:      '/devices',
            name:      'devices',
            component: () => import('@/views/device/Devices.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/devices/:imei',
            name:      'device',
            component: () => import('@/views/device/Device.vue'),
            meta:      { requiresAuth: true },
        },
        // The register the logbook's driver column points at. Every account reads it;
        // the page draws the administrators' half only for an administrator.
        {
            path:      '/drivers',
            name:      'drivers',
            component: () => import('@/views/driver/Drivers.vue'),
            meta:      { requiresAuth: true, kinds: FLEET },
        },
        // What each vehicle costs: the register and the overview built on it. Which of the
        // two is open lives in the address bar, so a page can be sent to somebody.
        {
            path:      '/costs',
            name:      'costs',
            component: () => import('@/views/cost/Costs.vue'),
            meta:      { requiresAuth: true, kinds: FLEET },
        },
        // The whole fleet's drives, by day and by vehicle — one call to GET /trips.
        {
            path:      '/trips',
            name:      'trips',
            component: () => import('@/views/trip/Trips.vue'),
            meta:      { requiresAuth: true, kinds: FLEET },
        },
        // The same drives and costs added up by vehicle, by driver and by day — one call to
        // GET /stats. Every account of a fleet reads it.
        {
            path:      '/stats',
            name:      'stats',
            component: () => import('@/views/stats/Stats.vue'),
            meta:      { requiresAuth: true, kinds: FLEET },
        },
        // What the console has noticed about the fleet — every account reads it, like
        // the drives themselves.
        {
            path:      '/alerts',
            name:      'alerts',
            component: () => import('@/views/alert/Alerts.vue'),
            meta:      { requiresAuth: true, kinds: FLEET },
        },
        // What is due on every vehicle and when. Every account reads it: a van whose
        // registration runs out on Friday is not a secret from the person driving it.
        {
            path:      '/reminders',
            name:      'reminders',
            component: () => import('@/views/reminder/Reminders.vue'),
            meta:      { requiresAuth: true, kinds: FLEET },
        },
        // What goes out by e-mail and when. An ADMINISTRATOR's: a schedule lists the
        // recipients, which are people's addresses, and one could post the fleet's
        // whole logbook to an address of somebody's choosing. `requiresAdmin` sends a
        // reader to the map rather than to a page whose every call answers 403.
        {
            path:      '/reports',
            name:      'reports',
            component: () => import('@/views/report/Reports.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true, kinds: FLEET },
        },
        // The geography the alarm rules are written against: every named place as the
        // circle it really is. Every account reads it; an administrator draws one.
        {
            path:      '/zones',
            name:      'zones',
            component: () => import('@/views/zone/Zones.vue'),
            meta:      { requiresAuth: true, kinds: FLEET },
        },
        // How to set a new FMC003 up so that it reports here, step by step. Every
        // account's: the person plugging a tracker in may be a reader, and the only
        // thing on it about this deployment (GET /install-info) is behind the login.
        {
            path:      '/install-guide',
            name:      'install-guide',
            component: () => import('@/views/guide/InstallGuide.vue'),
            meta:      { requiresAuth: true, kinds: PLATFORM },
        },
        // The service cases (docs/service-cases.md): every kind reads the ones in its
        // sight — Stanić all, a customer its fleet's, a workshop the ones assigned to it —
        // and the case page offers each party only its own steps.
        {
            path:      '/service-cases',
            name:      'service-cases',
            component: () => import('@/views/serviceCase/ServiceCases.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/service-cases/:id',
            name:      'service-case',
            component: () => import('@/views/serviceCase/ServiceCase.vue'),
            meta:      { requiresAuth: true },
        },
        // The parts the workshops order from Stanić against a case. A customer never sees
        // them (the API answers 403): what the workshop pays Stanić is between the two.
        {
            path:      '/part-orders',
            name:      'part-orders',
            component: () => import('@/views/partOrder/PartOrders.vue'),
            meta:      { requiresAuth: true, kinds: [OrganizationKind.Operator, OrganizationKind.Workshop] },
        },
        // "Vozila izvan pogona": Stanić's dashboard of the vehicles out of service now and
        // how long a vehicle takes to be operational again.
        {
            path:      '/out-of-service',
            name:      'out-of-service',
            component: () => import('@/views/outOfService/OutOfService.vue'),
            meta:      { requiresAuth: true, kinds: PLATFORM },
        },
        // The operator, the customers whose fleets he looks after, the workshops that
        // maintain them: the operator's administrators keep the register.
        {
            path:      '/organizations',
            name:      'organizations',
            component: () => import('@/views/organization/Organizations.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true, kinds: PLATFORM },
        },
        // Open to every account, not only to administrators: a reader manages their
        // own passkeys here, and this is the only page that does. The page itself
        // draws the administrators' half only for an administrator.
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true },
        },
    ],
})

// Where the page column stood on the page being left — read before the new page renders.
router.beforeEach((_to, from) => {
    const main = shellMain()

    if (main) shellScroll.set(from.fullPath, main.scrollTop)
})

router.beforeEach((to, _from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') return next({ name: 'map' })

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return next({ name: 'login' })
    }

    /*
     * An administrators-only screen. The role arrives with GET /me, which the default
     * layout asks for as it mounts, so on a cold load of /reports it is not known here
     * yet — and a guard that guessed would send an administrator away from their own
     * page. It refuses only what it can actually tell is a reader; the page itself
     * makes the same check once the answer is in, which is the one that always holds.
     */
    if (to.meta.requiresAdmin && appStore.userData && !appStore.isAdmin()) return next({ name: 'map' })

    // The same for a screen this kind of organisation does not use — once /me has said
    // which kind that is.
    const kinds = to.meta.kinds as OrganizationKind[] | undefined

    if (kinds && appStore.userData && !kinds.includes(appStore.organizationKind() as OrganizationKind)) return next({ name: 'map' })

    next()
})

// A redeploy replaces the hashed chunk files while a browser may still hold the old
// index.html. Every route here is lazy-loaded, so the app keeps painting and each
// navigation silently does nothing — indistinguishable from a broken button. Reload
// once when a chunk cannot be loaded, so the tab picks up the new build.
router.onError((error) => {
    if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(String(error))) {
        window.location.reload()
    }
})

export default router
